import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Eraser, Wallet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, BigButton } from "@/components/Field";
import { TextArea } from "@/components/Bits";
import { Pad } from "@/components/Signatures";
import { BRL } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";

const today = () => new Date().toISOString().slice(0, 10);

export const sendRecibo = async (pid) => {
  const win = window.open("", "_blank");
  try {
    const { data } = await api.post(`/pagamentos/${pid}/compartilhar`);
    const url = `https://wa.me/${data.telefone || ""}?text=${encodeURIComponent(data.mensagem)}`;
    if (win) win.location.href = url; else window.location.href = url;
    if (!data.telefone) toast.info("Técnico sem telefone cadastrado: escolha o contato no WhatsApp");
  } catch (e) { win?.close(); toast.error(errMsg(e)); }
};

export const PagarDialog = ({ open, onOpenChange, tecnicoId, rows, onSaved }) => {
  const [f, setF] = useState({ data: today(), observacao: "" });
  const [busy, setBusy] = useState(false);
  const total = rows.reduce((a, r) => a + r.valor, 0);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/pagamentos", { tecnico_id: tecnicoId, ...f,
        comissao_os_ids: rows.filter((r) => !r.bonus).map((r) => r.k), bonus_ids: rows.filter((r) => r.bonus).map((r) => r.k) });
      toast.success(`Pagamento registrado · ${data.numero}`);
      onOpenChange(false);
      onSaved(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="pagar-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Registrar pagamento</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-2xl bg-[#1E4D2B] text-white p-4">
            <p className="text-xs uppercase font-bold tracking-wider opacity-80">{rows.length} item(ns) selecionado(s)</p>
            <p className="font-display text-3xl font-extrabold" data-testid="pagar-total">{BRL(total)}</p>
          </div>
          <Field label="Data do pagamento *" id="pg-data" type="date" required value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} data-testid="pagar-data" />
          <TextArea label="Observação (forma de pagamento, etc.)" id="pg-obs" value={f.observacao} onChange={(e) => setF({ ...f, observacao: e.target.value })} data-testid="pagar-observacao" />
          <BigButton type="submit" disabled={busy} data-testid="pagar-confirm"><Wallet className="h-5 w-5" /> Confirmar e gerar recibo</BigButton>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const EditPagDialog = ({ pag, onOpenChange, onSaved }) => {
  const [f, setF] = useState({ data: "", observacao: "" });
  useEffect(() => { if (pag) setF({ data: pag.data, observacao: pag.observacao || "" }); }, [pag]);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.put(`/pagamentos/${pag.pagamento_id}`, f); toast.success("Pagamento atualizado"); onOpenChange(false); onSaved(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={!!pag} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="edit-pag-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Editar {pag?.numero}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Data do pagamento *" id="ep-data" type="date" required value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} data-testid="edit-pag-data" />
          <TextArea label="Observação" id="ep-obs" value={f.observacao} onChange={(e) => setF({ ...f, observacao: e.target.value })} data-testid="edit-pag-observacao" />
          <p className="text-xs text-[#5C6B66]">Alterar a data exige nova assinatura do técnico. Para mudar os itens, exclua e registre novamente.</p>
          <BigButton type="submit" data-testid="edit-pag-save">Salvar</BigButton>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ReciboSignDialog = ({ pag, onOpenChange, onSaved, defaultName }) => {
  const ref = useRef();
  const [nome, setNome] = useState("");
  useEffect(() => { if (pag) setNome(defaultName || ""); }, [pag, defaultName]);
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); c.dataset.dirty = ""; };
  const save = async () => {
    if (!ref.current.dataset.dirty) return toast.error("Faça a assinatura no quadro");
    try {
      await api.post(`/pagamentos/${pag.pagamento_id}/assinatura`, { nome, imagem: ref.current.toDataURL("image/png") });
      toast.success("Recibo assinado");
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!pag} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-3xl" data-testid="recibo-sign-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Assinar recibo {pag?.numero}</DialogTitle></DialogHeader>
        <p className="-mt-2 text-sm text-[#5C6B66]">Declaro ter recebido <b>{BRL(pag?.valor)}</b> referente às comissões e bônus deste recibo.</p>
        <div className="space-y-4">
          <Field label="Seu nome" id="rs-nome" value={nome} onChange={(e) => setNome(e.target.value)} data-testid="recibo-sign-name" />
          {pag && <Pad canvasRef={ref} />}
          <div className="grid grid-cols-2 gap-3">
            <BigButton type="button" variant="outline" onClick={clear} data-testid="recibo-sign-clear"><Eraser className="h-5 w-5" /> Limpar</BigButton>
            <BigButton type="button" onClick={save} disabled={nome.trim().length < 2} data-testid="recibo-sign-save">Assinar</BigButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
